'use client';

import { Check } from 'lucide-react';
import { motion } from 'motion/react';
import { useLocale } from '@/contexts/LocaleContext';
import type { LeaguePhase, Milestone } from '../types';
import { formatStageWhen, useWlKickoffTimes, useWlTimeZone } from '../wlTime';
import { LocalTimeNote } from './LocalTimeNote';

type StageStatus = 'done' | 'active' | 'upcoming';

const STATUS_BY_PHASE: Record<LeaguePhase, [StageStatus, StageStatus, StageStatus]> = {
  upcoming: ['upcoming', 'upcoming', 'upcoming'],
  entry_open: ['active', 'upcoming', 'upcoming'],
  qualifier_live: ['done', 'active', 'upcoming'],
  qualifier_done: ['done', 'done', 'upcoming'],
  playoffs_live: ['done', 'done', 'active'],
  completed: ['done', 'done', 'done'],
};

function Dot({ status, onGold }: { status: StageStatus; onGold: boolean }) {
  if (status === 'done') {
    return (
      <span className="flex size-7 items-center justify-center rounded-full bg-brand-green text-white">
        <Check className="size-4" strokeWidth={3} />
      </span>
    );
  }
  if (status === 'active') {
    // Gold on gold disappears — the active stage flips to brand blue there.
    return (
      <motion.span
        className={`flex size-7 items-center justify-center rounded-full ${onGold ? 'bg-brand-blue' : 'bg-brand-gold'}`}
        animate={{
          boxShadow: onGold
            ? ['0 0 0 0 rgba(45,66,255,0.45)', '0 0 0 8px rgba(45,66,255,0)']
            : ['0 0 0 0 rgba(255,215,0,0.5)', '0 0 0 8px rgba(255,215,0,0)'],
        }}
        transition={{ duration: 1.4, repeat: Infinity }}
      >
        <span className={`size-2 rounded-full ${onGold ? 'bg-white' : 'bg-black'}`} />
      </motion.span>
    );
  }
  return <span className="flex size-7 items-center justify-center rounded-full border-2 border-white/20 bg-surface-card-deep" />;
}

/** Friday → Saturday → Sunday stage rail, highlighting the current stage. */
export function ScheduleTimeline({
  phase,
  milestones,
  onGold = false,
}: {
  phase: LeaguePhase;
  milestones: Record<'entry' | 'qualifier' | 'playoffs', Milestone> | null;
  /** Rendered on the gold (entered) card — dark text/connector variants. */
  onGold?: boolean;
}) {
  const { t, locale } = useLocale();

  const timeZone = useWlTimeZone();
  const kickoff = useWlKickoffTimes();

  const statuses = STATUS_BY_PHASE[phase];
  // Real dates, never static schedule strings: a hardcoded "Mon 12:00" stayed
  // on screen while entry actually closed Friday 24:00 (owner reports
  // 2026-08-28). Milestones carry the tournament's own timestamps.
  const qualifierMs = milestones?.qualifier.targetMs ?? kickoff.qualifierMs;
  const stages: { title: string; when: string; m: Milestone | null }[] = [
    { title: t('weekendLeague.stageQualifying'), when: formatStageWhen(milestones?.entry.targetMs ?? kickoff.entryClosesMs, locale, timeZone), m: milestones?.entry ?? null },
    { title: t('weekendLeague.stageSaturday'), when: formatStageWhen(qualifierMs, locale, timeZone), m: milestones?.qualifier ?? null },
    { title: t('weekendLeague.stageFinal'), when: formatStageWhen(milestones?.playoffs.targetMs ?? kickoff.finalMs, locale, timeZone), m: milestones?.playoffs ?? null },
  ];

  return (
    <div>
      <div className="flex items-start">
        {stages.map((stage, i) => {
          const status = statuses[i];
          const emphasized = status !== 'upcoming';
          return (
            <div key={stage.title} className="flex flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                <span className={`h-0.5 flex-1 rounded-full ${i === 0 ? 'opacity-0' : statuses[i - 1] === 'done' ? 'bg-brand-green' : onGold ? 'bg-black/15' : 'bg-white/12'}`} />
                <Dot status={status} onGold={onGold} />
                <span className={`h-0.5 flex-1 rounded-full ${i === stages.length - 1 ? 'opacity-0' : status === 'done' ? 'bg-brand-green' : onGold ? 'bg-black/15' : 'bg-white/12'}`} />
              </div>
              <div className="mt-2 text-center">
                <div className={`font-poppins text-[11px] font-black uppercase tracking-wide ${emphasized ? (onGold ? 'text-black/85' : 'text-white') : onGold ? 'text-black/40' : 'text-white/45'}`}>
                  {stage.title}
                </div>
                <div className={`font-poppins text-[10px] font-semibold ${emphasized ? (onGold ? 'text-black/60' : 'text-white/70') : onGold ? 'text-black/30' : 'text-white/30'}`}>
                  {stage.when}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <LocalTimeNote
        kickoffMs={qualifierMs}
        timeZone={timeZone}
        className={`mt-2.5 text-center font-poppins text-[10px] font-semibold ${onGold ? 'text-black/55' : 'text-white/50'}`}
      />
    </div>
  );
}
